import type { ReconstructedMessage } from "@/shared/domain/admin";
import {
  type AgentThreadMessage,
  isAgentThreadToolResultContent,
  isAgentThreadToolUseContent,
} from "@/shared/domain/agentThread/AgentThreadRecord";

/**
 * The blocks to treat as a reconstructed message's payload for tool pairing and
 * display. For a `deleted`-tier entry the *sent* content is only a tombstone or
 * filler — the tool blocks that actually paired a request with its response
 * live on the verbatim original. Pairing (and the deleted-row view) keys off
 * those, so the request and its response collapse into one panel rather than
 * surfacing as two stray "deleted" rows. Every other tier renders what was
 * sent, so its own content is authoritative.
 */
export function effectiveContent(
  entry: ReconstructedMessage,
): AgentThreadMessage["content"] {
  if (entry.tier === "deleted" && entry.original_message !== null) {
    return entry.original_message.content;
  }
  return entry.message.content;
}

/**
 * Index, by `tool_use_id`, the reconstructed message holding each tool's result
 * and the set of ids that have a `tool_use` — so a `tool_use` can render
 * request + response in one panel and the standalone result message is skipped.
 * Keys off {@link effectiveContent}, so a deleted exchange (whose sent blocks
 * are tombstones) still pairs through its original tool blocks.
 */
export function indexToolPairs(messages: ReconstructedMessage[]): {
  resultEntryByToolUseId: Map<string, ReconstructedMessage>;
  pairedToolUseIds: Set<string>;
} {
  const resultEntryByToolUseId = new Map<string, ReconstructedMessage>();
  const pairedToolUseIds = new Set<string>();
  for (const entry of messages) {
    for (const block of effectiveContent(entry)) {
      if (isAgentThreadToolResultContent(block)) {
        resultEntryByToolUseId.set(block.tool_use_id, entry);
      }
      if (isAgentThreadToolUseContent(block)) {
        pairedToolUseIds.add(block.tool_use_id);
      }
    }
  }
  return { resultEntryByToolUseId, pairedToolUseIds };
}
